import type { i18n as I18nInstance } from 'i18next';
import { useLayoutEffect } from 'react';

import { BRAND_INTERPOLATION_VAR } from './brand.constants';
import { useBrandName } from './useBrandName';

type BrandI18nSyncProps = {
  /**
   * The app's i18next instance whose `brandName` interpolation default should
   * track the rebrand flag. Each app passes its own instance so the shared sync
   * logic stays decoupled from any single app's i18n config.
   */
  i18n: I18nInstance;
};

/**
 * Keeps the i18next `brandName` interpolation variable in sync with the rebrand
 * flag; renders nothing. Mount under FeatureFlagsProvider, passing the app's own
 * i18next instance. i18next exposes no public setter for interpolation
 * defaultVariables, so we write the option directly in a pre-paint layout effect
 * (only when it changes) and emit `languageChanged` to re-render consumers.
 */
const BrandI18nSync = ({ i18n }: BrandI18nSyncProps): null => {
  const brandName = useBrandName();

  useLayoutEffect(() => {
    const interpolation = i18n.options.interpolation ?? {};
    if (
      interpolation.defaultVariables?.[BRAND_INTERPOLATION_VAR] === brandName
    ) {
      return;
    }
    interpolation.defaultVariables = {
      ...interpolation.defaultVariables,
      [BRAND_INTERPOLATION_VAR]: brandName,
    };
    i18n.options.interpolation = interpolation;
    // `languageChanged` is what react-i18next listens to for re-rendering;
    // handlers read i18n.language directly, so no payload is needed.
    i18n.emit('languageChanged');
  }, [brandName, i18n]);

  return null;
};

export default BrandI18nSync;
